import React, { useState } from "react";
import {
  type StyleProp,
  type ViewStyle,
  type TextStyle,
} from "react-native";
import { Ionicons } from "@expo/vector-icons";
import { ThemedButton } from "@/components/ThemedButton";
import { MediaCaptureModal } from "@/components/media/MediaCaptureModal";
import { type ICapturedGeoLocation } from "@/services/media-capture";
import { trackMediaCaptured } from "@/services/analytics";
import { useThemeColor } from "@/hooks/useThemeColor";

/**
 * Properties for the standalone CaptureAudio component.
 */
export interface ICaptureAudioProps {
  /** Callback triggered when audio is captured */
  onCapture: (file: File | any, location?: ICapturedGeoLocation | null) => Promise<void> | void;
  /** Whether the capture action is disabled */
  disabled?: boolean;
  /** Whether an upload is currently processing */
  uploading?: boolean;
  /** Button title label (defaults to "Audio") */
  title?: string;
  /** Button style variant */
  variant?: "primary" | "secondary" | "minimal" | "danger";
  /** Optional container style override */
  style?: StyleProp<ViewStyle>;
  /** Optional text style override */
  textStyle?: StyleProp<TextStyle>;
  /** Custom trigger render function */
  renderTrigger?: (onPress: () => void, disabled: boolean) => React.ReactNode;
}

/**
 * Standalone Audio Capture component supporting high-accuracy geolocation tagging,
 * microphone recording via MediaCaptureModal (Web & Native via expo-audio),
 * and audio waveform/preview playback.
 *
 * @param {ICaptureAudioProps} props - Capture audio properties.
 * @returns {React.JSX.Element} The rendered CaptureAudio component.
 */
export function CaptureAudio({
  onCapture,
  disabled = false,
  uploading = false,
  title = "Audio",
  variant = "secondary",
  style,
  textStyle,
  renderTrigger,
}: ICaptureAudioProps): React.JSX.Element {
  const [modalVisible, setModalVisible] = useState(false);
  const tintColor = useThemeColor({}, "tint");
  const headerBgColor = useThemeColor({}, "buttonSecondary");
  const borderColor = useThemeColor({}, "border");

  const isDisabled = disabled || uploading;

  /**
   * Triggers audio recording modal.
   */
  const handleTrigger = (): void => {
    if (isDisabled) return;
    setModalVisible(true);
  };

  const handleCaptureComplete = (file: File | any, location?: ICapturedGeoLocation | null): void => {
    trackMediaCaptured("audio", undefined, Boolean(location));
    setModalVisible(false);
    onCapture(file, location);
  };

  return (
    <>
      {renderTrigger ? (
        renderTrigger(handleTrigger, isDisabled)
      ) : (
        <ThemedButton
          title={title}
          icon={<Ionicons name="mic-outline" size={16} color={tintColor} />}
          variant={variant}
          disabled={isDisabled}
          onPress={handleTrigger}
          style={[
            { backgroundColor: headerBgColor, borderColor },
            style,
          ]}
          textStyle={textStyle}
          accessibilityLabel="Record Audio"
          accessibilityRole="button"
        />
      )}

      {/* Audio Recorder Modal */}
      <MediaCaptureModal
        mode={modalVisible ? "audio" : null}
        onClose={() => setModalVisible(false)}
        onCaptureComplete={handleCaptureComplete}
      />
    </>
  );
}

export default CaptureAudio;
